import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ArrowUp, Check, ChevronDown, ChevronLeft, ChevronRight, Download, FileText, History, Loader2, Lock, Maximize2, MoreHorizontal, Plus, Sparkles, Trash2, Undo2, Unlock, X } from 'lucide-react';
import { agoLabel, canChangeIdea, type Idea, type WorkspacePerson } from '../../utils/ideas';
import {
  LINE_PT, MARGIN_PT, PAPER, addTurn, addVersion, appendText, applyAnswer, diffElements, fountainFile, minutesOf, paginate, parseFountain, reviewElements, scenesOf, summarizeDiff, withTitlePage,
  type ChatTurn, type DiffRow, type Paper, type Scene, type ScreenplayMeta,
} from '../../utils/screenplay';
import { fileNameFor, saveBlob, screenplayPdf } from '../../utils/screenplayPdf';
import { THINKING_LABELS, costOfUsage, estimateCostUsd, formatCost, formatCostShort, formatPrices, screenplayRequest, withThinking, type ScreenplayMode, type TextAnswer, type TextModelId, type TextModelOption, type TextRequest } from '../../utils/textModel';
import { ScreenplayEditor } from './ScreenplayEditor';
import { COURIER, PageView, TitlePageView } from './ScreenplayPages';
import { ReviewPages } from './ReviewPages';
import { BoardCard, errorText, useCloseOnOutside } from './parts';

/** What the card asks the page to do with the model */
export interface ScreenplayAsk {
  mode: ScreenplayMode;
  brief: string;
  pages: number;
  /** The scene to rewrite (mode 'rewrite') */
  scene?: Scene;
}

interface ScreenplayCardProps {
  idea: Idea;
  userId: string;
  userName: string;
  people: WorkspacePerson[];
  canEditWorkspace: boolean;
  /** The models the keys at hand can ask; none means no key */
  options: TextModelOption[];
  modelId: TextModelId | null;
  onModelChange: (id: TextModelId) => void;
  thinking?: string | null;
  onThinkingChange?: (effort: string) => void;
  /** The model is writing into this card */
  busy: boolean;
  now: number;
  /** Just made: the title takes the cursor */
  autoFocus?: boolean;
  onAutoFocused?: () => void;
  /** Lit up: what a link asked for */
  highlighted?: boolean;
  /** Opened full: the page in the middle, the chat at the right, the scenes down the left edge */
  focused?: boolean;
  onOpen?: () => void;
  onClose?: () => void;
  onSaveText: (text: string) => Promise<void>;
  onSaveMeta: (meta: ScreenplayMeta) => Promise<void>;
  /** The page runs the model and writes the answer in: a first draft, a scene rewritten, a continuation */
  onWrite: (ask: ScreenplayAsk) => Promise<void>;
  /** The page runs the model and hands the answer back: the chat shows what it would change, to keep or undo */
  onAskModel?: (request: TextRequest) => Promise<TextAnswer>;
  onKeep: (locked: boolean) => Promise<void>;
  onDelete: () => Promise<void>;
  /** The card has pages now, where it was the title page and a brief: the board may bring it into view whole */
  onPagesShown?: () => void;
}

/** The card is a sheet of paper wide: an A4 page at its real size, with the card's padding around it */
export const SCREENPLAY_CARD_WIDTH = 818;
/** Opened full, the page is at most its real size (96 pixels an inch) */
const FOCUS_SCALE_MAX = 96 / 72;
/** The chat down the right edge, opened full */
const CHAT_WIDTH = 380;
const SAVE_AFTER_MS = 700;
/** While someone types, a version is kept this often */
const SNAPSHOT_EVERY_MS = 10 * 60 * 1000;
const PAGE_GAP = 12;
const PAD_TOP = 8;
const LENGTHS = [1, 2, 3, 5, 8];

const DEFAULT_META: ScreenplayMeta = { title: '', author: '', paper: 'A4', draft: 1, versions: [] };

/** The day a moment falls on, for the chat's dividers */
const dayOf = (at: number, now: number): string => {
  const day = new Date(at);
  const today = new Date(now);
  const yesterday = new Date(now - 86_400_000);
  if (day.toDateString() === today.toDateString()) return 'Today';
  if (day.toDateString() === yesterday.toDateString()) return 'Yesterday';
  return day.toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
};

/**
 * A screenplay card: a sheet of A4 (or Letter) on the board, its text Fountain under it. On the
 * board the card shows one page at a time, turned with the pager, every line on it written in
 * place. Opened, it fills the screen: the pages scrolling in the middle, the scenes down the
 * left edge, the chat at the right, where the model is asked in plain words; what it would
 * change shows on the page until it is kept or undone. Versions keep themselves. Fountain shows
 * the words as text; the pages go out as PDF or Fountain.
 */
export const ScreenplayCard: React.FC<ScreenplayCardProps> = ({
  idea, userId, userName, people, canEditWorkspace, options, modelId, onModelChange, thinking = null, onThinkingChange,
  busy, now, autoFocus = false, onAutoFocused, highlighted = false, focused = false, onOpen, onClose, onSaveText, onSaveMeta, onWrite, onAskModel, onKeep, onDelete, onPagesShown,
}) => {
  const meta = idea.screenplay ?? DEFAULT_META;
  const metaRef = useRef(meta);
  metaRef.current = meta;
  const mayChange = canChangeIdea(idea, userId, canEditWorkspace, people);
  const available = options.length > 0;
  const model = options.find(option => option.id === modelId) ?? options[0] ?? null;
  const modelName = model?.name.replace(/ \(your .* key\)$/, '') ?? null;
  const efforts = model?.reasoning ? (model.efforts?.length ? model.efforts : ['low', 'medium', 'high']) : [];

  const [view, setView] = useState<'page' | 'text'>('page');
  const [draft, setDraft] = useState<string | null>(null);
  const [panel, setPanel] = useState<null | 'write' | 'versions' | 'export'>(null);
  const [page, setPage] = useState(1);
  /** How far down the pages the view is: its top, and a third of its height, for which scene is at hand */
  const [viewTop, setViewTop] = useState(0);
  const [editorPages, setEditorPages] = useState<number | null>(null);
  const [focusAtEnd, setFocusAtEnd] = useState(0);
  const [brief, setBrief] = useState('');
  const [scope, setScope] = useState<'scene' | 'whole' | 'continue'>('whole');
  const [pages, setPages] = useState(2);
  const [note, setNote] = useState('');
  const [title, setTitle] = useState<string | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [width, setWidth] = useState(SCREENPLAY_CARD_WIDTH - 38);
  // Opened full: the chat, what the model would change, and the small panels of the chat's head
  const [chatInput, setChatInput] = useState('');
  const [asking, setAsking] = useState(false);
  const [review, setReview] = useState<{ proposed: string; rows: DiffRow[]; turnAt: number; changed: number } | null>(null);
  const [chosen, setChosen] = useState<string | null>(null);
  /** Counts up each time the chosen lines are let go from the chat (the editor hears it) */
  const [letGo, setLetGo] = useState(0);
  const [modelOpen, setModelOpen] = useState(false);
  const [versionsOpen, setVersionsOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const [titlePageOpen, setTitlePageOpen] = useState(false);
  const bodyRef = useRef<HTMLDivElement>(null);
  const scrollerRef = useRef<HTMLDivElement>(null);
  const threadRef = useRef<HTMLDivElement>(null);
  const keepRef = useRef<HTMLButtonElement>(null);
  const titleRef = useRef<HTMLInputElement>(null);
  const pending = useRef<string | null>(null);
  const timer = useRef<number | null>(null);
  const closeModel = useCallback(() => setModelOpen(false), []);
  const closeVersions = useCallback(() => setVersionsOpen(false), []);
  const closeMore = useCallback(() => { setMoreOpen(false); setTitlePageOpen(false); }, []);
  const modelRef = useCloseOnOutside<HTMLDivElement>(modelOpen, closeModel);
  const versionsRef = useCloseOnOutside<HTMLDivElement>(versionsOpen, closeVersions);
  const moreRef = useCloseOnOutside<HTMLDivElement>(moreOpen || titlePageOpen, closeMore);

  const text = draft ?? idea.text;
  const parsed = useMemo(() => parseFountain(text), [text]);
  const laid = useMemo(() => paginate(parsed, meta.paper), [parsed, meta.paper]);
  const scenes = useMemo(() => scenesOf(text, laid), [text, laid]);
  const empty = !idea.text.trim() && meta.versions.length === 0;
  const editing = mayChange && view === 'page' && !review;
  /** The pages as they would be with the model's change: each line's element is its row */
  const reviewLaid = useMemo(() => (review ? paginate({ titlePage: parsed.titlePage, elements: reviewElements(review.rows), bodyStart: 0 }, meta.paper) : null), [review, parsed.titlePage, meta.paper]);
  const pageCount = reviewLaid ? reviewLaid.length : editing && editorPages !== null ? editorPages : laid.length;
  const { widthPt, heightPt } = PAPER[meta.paper];
  const scale = width / widthPt;
  const pageHeight = heightPt * scale;
  const hasTitlePage = !!parsed.titlePage.title;
  const sheetsBefore = hasTitlePage ? 1 : 0;
  /** Where each scene's heading lies in the scroller, from its top */
  const sceneTops = useMemo(() => {
    const tops = new Map<number, number>();
    laid.forEach((p, pi) => p.lines.forEach((line, i) => {
      if (line.kind === 'scene_heading' && !tops.has(line.scene)) tops.set(line.scene, PAD_TOP + (pi + sheetsBefore) * (pageHeight + PAGE_GAP) + (MARGIN_PT + i * LINE_PT) * scale);
    }));
    return tops;
  }, [laid, sheetsBefore, pageHeight, scale]);
  /** The scene at the top of the view: the last one whose heading is above the top third of it */
  const sceneHere = useMemo(() => {
    let found = scenes[0];
    for (const scene of scenes) {
      const top = sceneTops.get(scene.index);
      if (top !== undefined && top <= viewTop) found = scene; else break;
    }
    return found;
  }, [scenes, sceneTops, viewTop]);
  const turns = meta.chat ?? [];

  // The page fills the card's width, less the padding and the scrollbar; opened, the middle column's, up to its real size
  useEffect(() => {
    const el = bodyRef.current;
    if (!el) return;
    const measure = () => setWidth(Math.max(200, focused ? Math.min(el.clientWidth - 48, widthPt * FOCUS_SCALE_MAX) : el.clientWidth - 38));
    measure();
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [focused, empty, widthPt]);
  useEffect(() => { if (autoFocus) { titleRef.current?.focus(); onAutoFocused?.(); } }, [autoFocus, onAutoFocused]);
  // Someone else's change to the words is taken when nothing of ours is unsaved
  useEffect(() => { if (pending.current === null) setDraft(null); }, [idea.text]);
  useEffect(() => { if (page > pageCount) setPage(Math.max(1, pageCount)); }, [page, pageCount]);
  // From the title page and a brief to pages: the card grew a sheet taller
  const wasEmpty = useRef(empty);
  useEffect(() => {
    if (wasEmpty.current && !empty) onPagesShown?.();
    wasEmpty.current = empty;
  }, [empty, onPagesShown]);
  // Opened: Escape closes it, unless a line is being written (its own Escape leaves the line first) or a panel is open
  useEffect(() => {
    if (!focused) return;
    const onKey = (e: KeyboardEvent) => {
      const active = document.activeElement as HTMLElement | null;
      // Ctrl+A outside a field would light up the whole page (the browser's own select-all): there is nothing to choose that way here
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'a' && !active?.closest('[data-screenplay-editor], input, textarea, [contenteditable="true"]')) { e.preventDefault(); return; }
      if (e.key !== 'Escape') return;
      if (active?.closest('[data-screenplay-editor], input, textarea, select, [role="menu"]')) return;
      if (modelOpen || versionsOpen || moreOpen || titlePageOpen) { closeModel(); closeVersions(); closeMore(); return; }
      onClose?.();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [focused, onClose, modelOpen, versionsOpen, moreOpen, titlePageOpen, closeModel, closeVersions, closeMore]);
  // Opened or closed, the card gets a frame of its own for the pages, scrolled to the top: it is
  // brought to the page at hand (the sheets near that page alone are drawn, so a frame left at the
  // top would show nothing). Page 1 of a script with a title page shows the title page first, as before.
  useLayoutEffect(() => {
    const el = scrollerRef.current;
    if (!el || view !== 'page') return;
    const top = page === 1 && hasTitlePage ? 0 : PAD_TOP + (page - 1 + sheetsBefore) * (pageHeight + PAGE_GAP);
    el.scrollTop = top;
    setViewTop(top + el.clientHeight / 3);
    // The page itself is not a reason to scroll (goTo and the pager do that); the frame and its measure are
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focused, pageHeight, view, sheetsBefore]);
  // The chat shows its newest turn
  useEffect(() => { const el = threadRef.current; if (el) el.scrollTop = el.scrollHeight; }, [turns.length, asking, focused]);
  // A change shown on the page: the pages brought to the first line of it; Keep takes the keyboard
  // (the composer is quiet meanwhile), and nothing stays chosen on the page from before
  useEffect(() => {
    if (!review || !reviewLaid) return;
    try { window.getSelection()?.removeAllRanges(); } catch { /* nothing chosen */ }
    keepRef.current?.focus({ preventScroll: true });
    const el = scrollerRef.current;
    if (!el) return;
    for (let p = 0; p < reviewLaid.length; p++) {
      const i = reviewLaid[p].lines.findIndex(line => line.element >= 0 && review.rows[line.element]?.op !== 'same');
      if (i < 0) continue;
      const top = PAD_TOP + (p + sheetsBefore) * (pageHeight + PAGE_GAP) + (MARGIN_PT + i * LINE_PT) * scale;
      setPage(p + 1);
      setViewTop(top);
      el.scrollTo?.({ top: Math.max(0, top - el.clientHeight / 3), behavior: 'smooth' });
      return;
    }
  }, [review, reviewLaid, sheetsBefore, pageHeight, scale]);

  const versionNow = useCallback((noteText: string, by: string, byName: string, words: string): ScreenplayMeta => ({
    ...metaRef.current, versions: addVersion(metaRef.current.versions, { at: Date.now(), by, byName, note: noteText, text: words, pages: paginate(parseFountain(words), metaRef.current.paper).length }),
  }), []);

  const flush = useCallback(async () => {
    if (timer.current) { window.clearTimeout(timer.current); timer.current = null; }
    const words = pending.current;
    if (words === null) return;
    pending.current = null;
    try {
      await onSaveText(words);
      // Now and then while someone types, a version is kept of their own, so a bad hour can be undone
      const last = [...metaRef.current.versions].reverse().find(v => v.by === userId);
      if (!last || Date.now() - last.at > SNAPSHOT_EVERY_MS) await onSaveMeta(versionNow('you edited', userId, userName, words));
    } catch (e) { setProblem(errorText(e, 'The screenplay could not be saved.')); }
  }, [onSaveText, onSaveMeta, userId, userName, versionNow]);
  useEffect(() => () => { void flush(); }, [flush]);
  const queue = useCallback((words: string) => {
    setDraft(words);
    pending.current = words;
    if (timer.current) window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => { void flush(); }, SAVE_AFTER_MS);
  }, [flush]);

  const act = (fn: () => Promise<void>, fallback: string) => { setProblem(null); void fn().catch((e) => setProblem(errorText(e, fallback))); };
  const saveTitlePage = (fields: { title?: string; author?: string }) => act(async () => {
    const next = { ...metaRef.current, ...fields };
    await onSaveMeta(next);
    if (idea.text.trim()) await onSaveText(withTitlePage(idea.text, { title: next.title, author: next.author }));
  }, 'The title page could not be saved.');

  const goTo = useCallback((n: number) => {
    const target = Math.min(Math.max(1, pageCount), Math.max(1, n));
    const top = PAD_TOP + (target - 1 + sheetsBefore) * (pageHeight + PAGE_GAP);
    setPage(target);
    setViewTop(top + (scrollerRef.current?.clientHeight ?? 0) / 3);
    scrollerRef.current?.scrollTo?.({ top, behavior: 'smooth' });
  }, [pageCount, sheetsBefore, pageHeight]);
  const onScroll = () => {
    const el = scrollerRef.current;
    if (!el) return;
    const index = Math.round((el.scrollTop - PAD_TOP) / (pageHeight + PAGE_GAP)) - sheetsBefore;
    setPage(Math.min(Math.max(1, pageCount), Math.max(1, index + 1)));
    // Scrolled to the very end, the last scene is the one at hand, however short the page's tail
    const atEnd = el.scrollHeight > el.clientHeight && el.scrollTop + el.clientHeight >= el.scrollHeight - 2;
    setViewTop(atEnd ? Infinity : el.scrollTop + el.clientHeight / 3);
  };
  const onLaidOut = useCallback(({ pages: n }: { pages: number }) => setEditorPages(n), []);
  /** The pages scrolled to a scene's heading, a little room above it */
  const goToScene = (scene: Scene) => {
    const heading = sceneTops.get(scene.index);
    if (heading === undefined) return;
    setPage(scene.page);
    // The scene asked for is the one at hand from this moment; the scroll that follows agrees
    setViewTop(heading + (scrollerRef.current?.clientHeight ?? 0) / 3);
    scrollerRef.current?.scrollTo?.({ top: Math.max(0, heading - 24), behavior: 'smooth' });
  };
  /** A new scene at the end, its heading begun */
  const addScene = () => act(async () => {
    await flush();
    await onSaveText(appendText(text, 'INT. '));
    setFocusAtEnd(n => n + 1);
  }, 'The scene could not be added.');

  const exportAs = (what: 'pdf-A4' | 'pdf-Letter' | 'fountain') => act(async () => {
    setExporting(true);
    try {
      const name = meta.title || parsed.titlePage.title || 'Screenplay';
      if (what === 'fountain') saveBlob(fountainFile(text), fileNameFor(name, 'fountain'));
      else {
        const paper: Paper = what === 'pdf-A4' ? 'A4' : 'Letter';
        await screenplayPdf(paginate(parsed, paper), { ...parsed.titlePage, title: parsed.titlePage.title || meta.title, author: parsed.titlePage.author || meta.author }, paper, fileNameFor(name, 'pdf'));
      }
      setPanel(null);
      setMoreOpen(false);
    } finally { setExporting(false); }
  }, 'The file could not be made.');

  const request = useMemo(() => {
    const mode: ScreenplayMode = empty ? 'write' : scope === 'scene' ? 'rewrite' : scope === 'continue' ? 'continue' : 'write';
    return withThinking(screenplayRequest(mode, { brief, pages, title: meta.title, author: meta.author, text, sceneHeading: sceneHere?.heading, sceneText: sceneHere ? text.split('\n').slice(sceneHere.from, sceneHere.to).join('\n') : undefined }), model, thinking);
  }, [empty, scope, brief, pages, meta.title, meta.author, text, sceneHere, model, thinking]);
  const cost = model ? estimateCostUsd(model, request) : null;
  /** What the next asking in the chat would cost, at most */
  const blank = !text.trim();
  const chatCost = useMemo(() => (model ? estimateCostUsd(model, withThinking(blank ? screenplayRequest('write', { brief: chatInput, pages }) : screenplayRequest('edit', { brief: chatInput, pages, text, selection: chosen ?? undefined }), model, thinking)) : null), [model, blank, chatInput, pages, text, chosen, thinking]);

  const ask = () => act(async () => {
    await flush();
    const mode: ScreenplayMode = empty ? 'write' : scope === 'scene' ? 'rewrite' : scope === 'continue' ? 'continue' : 'write';
    await onWrite({ mode, brief, pages, scene: mode === 'rewrite' ? sceneHere : undefined });
    setPanel(null);
    setBrief('');
  }, 'The model did not answer.');

  /** A page to write on by hand: the title page and FADE IN, the caret after it */
  const startWriting = () => act(async () => {
    await onSaveText(withTitlePage('FADE IN:\n\n', { title: meta.title, author: meta.author }));
    setFocusAtEnd(n => n + 1);
  }, 'The screenplay could not be started.');

  // ── The chat: the model asked in plain words, with the whole script as its context ──
  const saveTurn = async (turn: ChatTurn) => { await onSaveMeta({ ...metaRef.current, chat: addTurn(metaRef.current.chat, turn) }); };
  const send = () => {
    const words = chatInput.trim();
    if (!words || asking || !mayChange || !model) return;
    setChatInput('');
    setProblem(null);
    setAsking(true);
    const turn: ChatTurn = { at: Date.now(), ask: words, note: '', model: modelName ?? 'a model', usd: null, status: 'pending' };
    void (async () => {
      try {
        await saveTurn(turn);
        await flush();
        if (blank) {
          // Nothing on the page yet: the words are the brief, and the draft is written straight in
          await onWrite({ mode: 'write', brief: words, pages });
          turn.status = 'written';
          turn.note = 'A first draft, written from your words.';
        } else if (onAskModel) {
          // The page runs the model with the thinking chosen in the chat's head; the whole script is all the model is given
          const answer = await onAskModel(screenplayRequest('edit', { brief: words, pages, title: meta.title, author: meta.author, text, selection: chosen ?? undefined }));
          turn.usd = costOfUsage(model, answer);
          // The scenes it changed, put in their places; the rest of the script is as it was
          const proposed = applyAnswer(text, answer.text);
          if (!proposed.trim()) throw new Error('The model wrote nothing. Try again.');
          const rows = diffElements(parseFountain(text).elements, parseFountain(proposed).elements);
          const summary = summarizeDiff(rows);
          if (summary.changed === 0) { turn.status = 'undone'; turn.note = 'Nothing changed.'; }
          else { turn.note = `${summary.note}, shown on the page.`; setReview({ proposed, rows, turnAt: turn.at, changed: summary.changed }); setView('page'); }
        } else throw new Error('The model cannot be asked here.');
      } catch (e) {
        turn.status = 'failed';
        turn.note = errorText(e, 'The model did not answer.');
      } finally {
        setAsking(false);
        await saveTurn(turn).catch((e) => setProblem(errorText(e, 'The chat could not be saved.')));
      }
    })();
  };
  /** The model's change taken: the script as it made it, kept as a version */
  const keepChanges = () => act(async () => {
    if (!review) return;
    const turn = turns.find(t => t.at === review.turnAt);
    await onSaveText(review.proposed);
    const kept = versionNow(turn?.ask ?? 'the model\'s change', 'ai', modelName ?? 'a model', review.proposed);
    await onSaveMeta({ ...kept, chat: turn ? addTurn(kept.chat, { ...turn, status: 'kept' }) : kept.chat });
    setReview(null);
  }, 'The change could not be kept.');
  const undoChanges = () => act(async () => {
    if (!review) return;
    const turn = turns.find(t => t.at === review.turnAt);
    setReview(null);
    if (turn) await saveTurn({ ...turn, status: 'undone' });
  }, 'The chat could not be saved.');

  const modelControls = (
    <div className="flex flex-wrap items-center gap-2" data-screenplay-model>
      <select value={model?.id ?? ''} onChange={(e) => onModelChange(e.target.value)} aria-label="Which model" className="ns-field ns-field-sm min-w-0 flex-1">
        {options.map(option => { const prices = formatPrices(option); return <option key={option.id} value={option.id}>{option.name}{prices ? ` · ${prices}` : ''}</option>; })}
      </select>
      {efforts.length > 0 && (
        <select value={thinking ?? ''} onChange={(e) => onThinkingChange?.(e.target.value)} aria-label="How hard the model thinks" className="ns-field ns-field-sm w-[108px]">
          {thinking === null && <option value="">The model's own</option>}
          {efforts.map(effort => <option key={effort} value={effort}>{THINKING_LABELS[effort] ?? effort}</option>)}
        </select>
      )}
    </div>
  );

  const headTitle = title !== null ? (
    <input ref={titleRef} type="text" value={title} autoFocus maxLength={120} aria-label="Title" placeholder="Title" onChange={(e) => setTitle(e.target.value)} onBlur={() => { const next = title.trim(); setTitle(null); if (next !== meta.title) saveTitlePage({ title: next }); }} onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); if (e.key === 'Escape') setTitle(null); }} className="ns-field ns-field-sm min-w-0 flex-1 h-7 py-0" data-screenplay-title-field />
  ) : (
    <button type="button" disabled={!mayChange} onClick={() => setTitle(meta.title || parsed.titlePage.title || '')} className="canvas-node-title text-left min-w-0 flex-1" title={mayChange ? 'Rename' : undefined} data-screenplay-title>
      {meta.title || parsed.titlePage.title || <span className="opacity-60">Untitled screenplay</span>}
    </button>
  );

  const viewSwitch = (
    <div className="ns-seg" role="radiogroup" aria-label="Pages or Fountain">
      {(['page', 'text'] as const).map(v => <button key={v} type="button" role="radio" aria-checked={view === v} className="ns-seg-item" onClick={() => { void flush(); setView(v); }}>{v === 'page' ? 'Pages' : 'Fountain'}</button>)}
    </div>
  );
  const pager = (
    <div className="flex items-center gap-0.5" data-screenplay-pager>
      <button type="button" onClick={() => goTo(page - 1)} disabled={page <= 1} className="ns-icon-btn ns-icon-btn-xs" aria-label="Previous page"><ChevronLeft /></button>
      <span className="text-xs font-semibold tabular-nums min-w-[48px] text-center">{page} / {pageCount}</span>
      <button type="button" onClick={() => goTo(page + 1)} disabled={page >= pageCount} className="ns-icon-btn ns-icon-btn-xs" aria-label="Next page"><ChevronRight /></button>
    </div>
  );
  const paperSwitch = (
    <div className="ns-seg" role="radiogroup" aria-label="Paper">
      {(['A4', 'Letter'] as const).map(paper => <button key={paper} type="button" role="radio" aria-checked={meta.paper === paper} disabled={!mayChange} className="ns-seg-item" onClick={() => act(() => onSaveMeta({ ...metaRef.current, paper }), 'The paper could not be set.')}>{paper}</button>)}
    </div>
  );
  const exportChoices = (
    <div className="flex flex-wrap items-center gap-1.5" data-screenplay-export>
      <button type="button" disabled={exporting} onClick={() => exportAs('pdf-A4')} className="ns-btn ns-btn-xs ns-btn-soft ns-btn-flat">PDF · A4</button>
      <button type="button" disabled={exporting} onClick={() => exportAs('pdf-Letter')} className="ns-btn ns-btn-xs ns-btn-soft ns-btn-flat">PDF · Letter</button>
      <button type="button" disabled={exporting} onClick={() => exportAs('fountain')} className="ns-btn ns-btn-xs ns-btn-soft ns-btn-flat">Fountain</button>
      {exporting && <Loader2 size={14} className="animate-spin text-ink-faint" aria-label="Making the file" />}
      <span className="ns-t-meta basis-full">PDF in Courier 12 with the industry's margins; Fountain opens in Highland, Slugline and Final Draft.</span>
    </div>
  );
  const composer = (
    <div className="flex flex-col gap-2" data-screenplay-composer>
      <textarea value={brief} onChange={(e) => setBrief(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter' && (e.ctrlKey || e.metaKey) && !busy) { e.preventDefault(); ask(); } }} rows={3} placeholder={scope === 'scene' ? 'What to change in this scene' : scope === 'continue' ? 'Where it goes next (optional)' : 'A brief for a fresh draft of the whole script'} className="ns-field w-full resize-none font-serif text-[15px] leading-7" data-screenplay-brief />
      <div className="flex flex-wrap items-center gap-2">
        <div className="ns-seg" role="radiogroup" aria-label="What to write">
          {([['scene', sceneHere ? `Scene ${sceneHere.index + 1}` : 'This scene'], ['whole', 'Whole script'], ['continue', 'Continue']] as const).map(([value, label]) => (
            <button key={value} type="button" role="radio" aria-checked={scope === value} disabled={value === 'scene' && !sceneHere} className="ns-seg-item" onClick={() => setScope(value)}>{label}</button>
          ))}
        </div>
        {scope !== 'scene' && (
          <select value={pages} onChange={(e) => setPages(Number(e.target.value))} aria-label="How many pages" className="ns-field ns-field-sm w-[120px]">
            {LENGTHS.map(n => <option key={n} value={n}>{n} page{n === 1 ? '' : 's'}</option>)}
          </select>
        )}
      </div>
      {modelControls}
      <div className="flex items-center gap-2">
        {cost !== null && <span className="ns-t-meta flex-1" data-cost-estimate>Up to {formatCost(cost)}.{scope === 'whole' ? ' The whole script is written anew, as a new draft; the one here is kept as a version.' : ''}</span>}
        <button type="button" disabled={busy} onClick={ask} className="ns-btn ns-btn-sm ns-btn-primary ns-btn-flat ml-auto" data-screenplay-ask>
          {busy ? <Loader2 size={14} className="animate-spin" aria-hidden="true" /> : <Sparkles size={14} aria-hidden="true" />}
          {busy ? 'Writing' : `Ask ${modelName ?? ''}`.trim()}
        </button>
      </div>
    </div>
  );
  const versionList = meta.versions.length === 0 ? <p className="ns-t-meta px-2">No versions yet. One is kept each time the model writes, when you keep its changes, when you save one, and now and then while you type.</p> : (
    <ul className="flex flex-col gap-0.5 overflow-y-auto max-h-[320px]" data-screenplay-version-list data-scrolls>
      {[...meta.versions].reverse().map((v, i) => (
        <li key={`${v.at}-${i}`} className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-ink/[0.04] text-xs">
          <span className={`ns-mark ns-mark-sm ${v.by === 'ai' ? 'ns-mark-lavender' : ''}`} aria-hidden="true">{v.by === 'ai' ? <Sparkles /> : <span className="text-2xs font-bold">{v.byName.slice(0, 1).toUpperCase()}</span>}</span>
          <span className="min-w-0 flex-1">
            <span className="block font-semibold text-ink truncate">{v.note}</span>
            <span className="block text-ink-soft">{v.byName} · {agoLabel(v.at, now)} · {v.pages} page{v.pages === 1 ? '' : 's'}</span>
          </span>
          {mayChange && v.text !== idea.text && (
            <button type="button" onClick={() => act(async () => { await onSaveText(v.text); await onSaveMeta(versionNow(`restored: ${v.note}`, userId, userName, v.text)); setDraft(null); setReview(null); setVersionsOpen(false); }, 'The version could not be restored.')} className="ns-btn ns-btn-xs ns-btn-soft ns-btn-flat" data-screenplay-restore>Restore</button>
          )}
        </li>
      ))}
    </ul>
  );
  const saveVersion = mayChange && (
    <div className="flex items-center gap-2">
      <input type="text" value={note} onChange={(e) => setNote(e.target.value)} maxLength={120} placeholder="A note for this version" aria-label="A note for this version" className="ns-field ns-field-sm flex-1 min-w-0" />
      <button type="button" onClick={() => act(async () => { await flush(); await onSaveMeta(versionNow(note.trim() || 'saved by you', userId, userName, text)); setNote(''); }, 'The version could not be saved.')} className="ns-btn ns-btn-xs ns-btn-primary ns-btn-flat" data-screenplay-save-version>Save version</button>
    </div>
  );
  const fountainField = (
    <textarea
      value={text}
      readOnly={!mayChange}
      onChange={(e) => queue(e.target.value)}
      onBlur={() => void flush()}
      spellCheck={false}
      aria-label="The screenplay, in Fountain"
      className="ns-field w-full resize-none text-xs leading-5 text-ink"
      style={{ height: focused ? '100%' : pageHeight + 16, fontFamily: COURIER }}
      data-screenplay-text
      data-scrolls
    />
  );
  /** The pages, in the card's scroller: written on in place, as the model would change them, or as printed for someone who may not change them */
  const pagesInScroller = (
    <>
      {(busy || asking) && (
        <div className="sticky top-2 z-10 mx-auto w-max flex items-center gap-2 ns-bar bg-white/95 border border-ink/10 px-3 py-1.5 text-xs font-semibold text-ink" data-screenplay-writing>
          <Loader2 size={14} className="animate-spin" aria-hidden="true" /> Writing{modelName ? ` · ${modelName}` : ''}
        </div>
      )}
      {review ? (
        <div className="mx-auto" style={{ width: widthPt * scale }}>
          <ReviewPages rows={review.rows} titlePage={parsed.titlePage} paper={meta.paper} scale={scale} pageGap={PAGE_GAP} padTop={PAD_TOP} />
        </div>
      ) : editing ? (
        <div className="mx-auto" style={{ width: widthPt * scale }}>
          <ScreenplayEditor text={text} paper={meta.paper} scale={scale} pageGap={PAGE_GAP} padTop={PAD_TOP} visiblePage={page} onChange={queue} onLaidOut={onLaidOut} onRequestPage={goTo} focusAtEnd={focusAtEnd} onSelectionChange={focused ? setChosen : undefined} letGo={letGo} />
        </div>
      ) : (
        <div className="flex flex-col items-center" style={{ gap: PAGE_GAP, padding: `${PAD_TOP}px 0 ${PAGE_GAP}px` }}>
          {hasTitlePage && <TitlePageView titlePage={parsed.titlePage} paper={meta.paper} scale={scale} />}
          {laid.map(p => <PageView key={p.number} page={p} paper={meta.paper} scale={scale} />)}
        </div>
      )}
    </>
  );

  // ── Opened full ──
  const chosenLines = chosen ? chosen.split('\n').filter(line => line.trim()).length : 0;
  const pendingTurn = review ? turns.find(t => t.at === review.turnAt) : undefined;
  const focusView = focused && typeof document !== 'undefined' ? createPortal(
    <div className="fixed inset-0 z-40 bg-cream text-ink font-sans" style={{ backgroundImage: 'radial-gradient(rgb(var(--c-ink) / 0.10) 1px, transparent 1px)', backgroundSize: '24px 24px' }} data-screenplay-focus>
      {/* The scenes, down the left edge */}
      <nav aria-label="Scenes" className="ns-glass ns-glass-strong absolute left-4 top-[72px] z-10 flex flex-col items-center gap-1 rounded-3xl p-1 max-h-[calc(100vh-160px)] overflow-y-auto no-scrollbar" data-screenplay-scenes>
        {scenes.map(scene => (
          <button
            key={scene.index}
            type="button"
            onClick={() => goToScene(scene)}
            aria-current={scene === sceneHere ? 'true' : undefined}
            title={scene.heading}
            className={`relative w-8 h-8 shrink-0 rounded-full grid place-items-center text-xs font-bold ${scene === sceneHere ? 'bg-ink text-white' : 'text-ink hover:bg-cream-200'}`}
            data-screenplay-scene={scene.index + 1}
          >
            {scene.index + 1}
            <span className={`absolute left-2 right-2 bottom-1 h-0.5 rounded-sm overflow-hidden ${scene === sceneHere ? 'bg-white/30' : 'bg-ink/10'}`} aria-hidden="true">
              <span className={`block h-full ${scene === sceneHere ? 'bg-white' : 'bg-lavender'}`} style={{ width: `${Math.min(100, Math.round((scene.lines / PAPER[meta.paper].linesPerPage) * 100))}%` }} />
            </span>
          </button>
        ))}
        {mayChange && !review && <button type="button" onClick={addScene} className="ns-icon-btn ns-icon-btn-sm shrink-0" aria-label="New scene at the end" title="A new scene at the end"><Plus /></button>}
      </nav>

      {/* Over the page: the way back, the name, where you are, what the model changed */}
      <div className="absolute top-4 z-10 -translate-x-1/2 ns-glass ns-glass-strong ns-bar flex items-center gap-2.5 h-[42px] pl-1.5 pr-4 max-w-[calc(100vw-520px)]" style={{ left: `calc((100% - ${CHAT_WIDTH + 32}px + 72px) / 2)` }} data-screenplay-focus-bar>
        <button type="button" onClick={onClose} className="ns-btn ns-btn-xs ns-btn-soft ns-btn-flat shrink-0" data-screenplay-back><ChevronLeft size={14} aria-hidden="true" />Back to the board</button>
        <span className="min-w-0 flex items-center gap-2">{headTitle}</span>
        <span className="ns-badge shrink-0" data-screenplay-draft>Draft {meta.draft}</span>
        <span className="ns-t-meta whitespace-nowrap shrink-0" data-screenplay-where>{sceneHere ? `Scene ${sceneHere.index + 1} · ` : ''}p. {page} of {pageCount}</span>
        {review && <span className="flex items-center gap-1.5 text-xs font-bold text-lavender-deep whitespace-nowrap shrink-0" data-screenplay-changed><span className="w-2 h-2 rounded-sm bg-lavender-soft border border-lavender" aria-hidden="true" />{review.changed} line{review.changed === 1 ? '' : 's'} changed</span>}
      </div>

      {/* The pages, scrolling */}
      <div className="absolute top-[72px] bottom-4 left-[72px]" style={{ right: CHAT_WIDTH + 32 }}>
        <div ref={bodyRef} className="relative h-full">
          {view === 'page' ? (
            <div ref={scrollerRef} onScroll={onScroll} className="h-full overflow-y-auto overflow-x-hidden overscroll-contain" data-screenplay-pages data-pages-scroller data-scrolls>
              {pagesInScroller}
            </div>
          ) : <div className="h-full pb-14">{fountainField}</div>}
          <div className="absolute bottom-3 left-1/2 -translate-x-1/2 z-10 ns-glass ns-glass-strong ns-bar flex items-center gap-2 p-1 pr-2">
            {pager}
            {viewSwitch}
          </div>
        </div>
      </div>

      {/* The chat, down the right edge */}
      <aside aria-label="Ask" className="ns-glass ns-glass-strong absolute top-[72px] bottom-4 right-4 rounded-2xl flex flex-col overflow-visible z-10" style={{ width: CHAT_WIDTH }} data-screenplay-chat>
        <div className="flex items-center gap-2 h-12 px-3 border-b border-ink/5 shrink-0">
          <div className="relative min-w-0" ref={modelRef}>
            <button type="button" onClick={() => setModelOpen(open => !open)} aria-expanded={modelOpen} className="flex items-center gap-2 min-w-0 rounded-lg pr-1" title="The model, and how hard it thinks" data-screenplay-model-pick>
              <span className="ns-mark ns-mark-sm ns-mark-lavender shrink-0"><Sparkles /></span>
              <span className="text-sm font-bold text-ink truncate">{modelName ?? 'No model'}</span>
              <ChevronDown size={12} className="text-ink-soft shrink-0" aria-hidden="true" />
            </button>
            {modelOpen && (
              <div className="ns-popover absolute left-0 top-full mt-2 w-[340px] p-3 flex flex-col gap-2 z-20">
                {available ? modelControls : <p className="ns-t-meta">The model needs a Venice, Google or OpenAI key, added in Settings.</p>}
                {efforts.length > 0 && <p className="ns-t-meta">Deeper thinking is slower and costs more; light thinking is enough for most changes.</p>}
              </div>
            )}
          </div>
          <span className="flex-1" />
          <div className="relative" ref={versionsRef}>
            <button type="button" onClick={() => setVersionsOpen(open => !open)} aria-expanded={versionsOpen} className="ns-chip ns-chip-sm ns-chip-btn" data-screenplay-versions-open><History size={12} aria-hidden="true" />Versions · {meta.versions.length}</button>
            {versionsOpen && (
              <div className="ns-popover absolute right-0 top-full mt-2 w-[340px] p-2 flex flex-col gap-2 z-20" data-screenplay-versions>
                <p className="ns-t-label px-2 pt-1">Versions · {meta.versions.length}</p>
                {versionList}
                {saveVersion}
                <p className="ns-t-meta px-2 pb-1">Kept by themselves: each time the model writes, each time you keep its changes, and every ten minutes while you write. Restoring makes a new version; nothing is lost.</p>
              </div>
            )}
          </div>
          <div className="relative" ref={moreRef}>
            <button type="button" onClick={() => { setMoreOpen(open => !open); setTitlePageOpen(false); }} aria-expanded={moreOpen} className="ns-icon-btn ns-icon-btn-sm" aria-label="More" data-screenplay-more><MoreHorizontal /></button>
            {moreOpen && !titlePageOpen && (
              <div className="ns-menu absolute right-0 top-full mt-2 w-[230px] z-20" role="menu">
                <button type="button" role="menuitem" disabled={exporting} onClick={() => exportAs('pdf-A4')} className="ns-menu-item"><Download /> Export PDF <span className="text-ink-soft">· A4</span></button>
                <button type="button" role="menuitem" disabled={exporting} onClick={() => exportAs('pdf-Letter')} className="ns-menu-item"><Download /> Export PDF <span className="text-ink-soft">· Letter</span></button>
                <button type="button" role="menuitem" disabled={exporting} onClick={() => exportAs('fountain')} className="ns-menu-item"><FileText /> Fountain file</button>
                <div className="ns-menu-sep" />
                {(['A4', 'Letter'] as const).map(paper => (
                  <button key={paper} type="button" role="menuitemradio" aria-checked={meta.paper === paper} disabled={!mayChange} onClick={() => { act(() => onSaveMeta({ ...metaRef.current, paper }), 'The paper could not be set.'); setMoreOpen(false); }} className="ns-menu-item">Paper {paper}{meta.paper === paper && <Check className="ml-auto" />}</button>
                ))}
                <button type="button" role="menuitem" disabled={!mayChange} onClick={() => setTitlePageOpen(true)} className="ns-menu-item">Title page…</button>
                <div className="ns-menu-sep" />
                <button type="button" role="menuitem" onClick={onClose} className="ns-menu-item"><ChevronLeft /> Back to the board</button>
                {mayChange && <button type="button" role="menuitem" data-danger="true" onClick={() => { setMoreOpen(false); setConfirmDelete(true); }} className="ns-menu-item"><Trash2 /> Delete screenplay</button>}
              </div>
            )}
            {titlePageOpen && (
              <div className="ns-popover absolute right-0 top-full mt-2 w-[300px] p-3 flex flex-col gap-2 z-20" data-screenplay-title-page>
                <p className="ns-t-label">Title page</p>
                <label className="flex flex-col gap-1"><span className="ns-t-label">Title</span><input type="text" defaultValue={meta.title || parsed.titlePage.title || ''} maxLength={120} onBlur={(e) => { if (e.target.value.trim() !== meta.title) saveTitlePage({ title: e.target.value.trim() }); }} className="ns-field ns-field-sm" data-screenplay-field="title" /></label>
                <label className="flex flex-col gap-1"><span className="ns-t-label">Written by</span><input type="text" defaultValue={meta.author || parsed.titlePage.author || ''} maxLength={120} onBlur={(e) => { if (e.target.value.trim() !== meta.author) saveTitlePage({ author: e.target.value.trim() }); }} className="ns-field ns-field-sm" data-screenplay-field="author" /></label>
                <button type="button" onClick={closeMore} className="ns-btn ns-btn-xs ns-btn-primary ns-btn-flat self-end">Done</button>
              </div>
            )}
          </div>
        </div>
        {confirmDelete && (
          <div className="ns-note m-3" data-tone="danger" data-confirm-delete>
            <span className="flex-1">Delete this screenplay and its {meta.versions.length} version{meta.versions.length === 1 ? '' : 's'}?</span>
            <button type="button" onClick={() => { setConfirmDelete(false); act(() => onDelete(), 'The screenplay could not be deleted.'); }} className="ns-btn ns-btn-xs ns-btn-danger ns-btn-flat">Delete</button>
            <button type="button" onClick={() => setConfirmDelete(false)} className="ns-btn ns-btn-xs ns-btn-soft ns-btn-flat">Keep</button>
          </div>
        )}
        {/* The thread, newest at the bottom */}
        <div ref={threadRef} className="flex-1 min-h-0 overflow-y-auto overscroll-contain flex flex-col gap-2.5 px-3 py-3" data-scrolls data-screenplay-thread>
          {/* A short thread sits at the bottom, by the asking */}
          <span className="flex-1" aria-hidden="true" />
          {turns.length === 0 && !asking && (
            <div className="ns-note" data-tone="lavender">
              {empty ? 'Write on the page, or ask here for a first draft: "A 2 minute short: a small fox who cannot keep a lantern lit."' : 'Ask for a scene, a rewrite, a cut. The model reads the whole script, changes what you ask, and shows the change on the page for you to keep or undo.'}
            </div>
          )}
          {turns.map((turn, i) => {
            const day = dayOf(turn.at, now);
            const newDay = i === 0 || dayOf(turns[i - 1].at, now) !== day;
            const live = review && review.turnAt === turn.at;
            return (
              <React.Fragment key={turn.at}>
                {newDay && <div className="self-center ns-t-label">{day}</div>}
                <div className="self-end max-w-[86%] px-3 py-2 rounded-2xl rounded-br-md bg-ink text-cream text-[13px] leading-snug whitespace-pre-wrap [overflow-wrap:anywhere]" data-screenplay-turn={turn.at}>{turn.ask}</div>
                {(turn.note || turn.status !== 'pending' || live) && (
                  <div className="self-start max-w-[92%] flex flex-col gap-2 px-3 py-2 rounded-2xl rounded-bl-md bg-white border border-ink/10 text-[13px] leading-snug" data-screenplay-reply={turn.status}>
                    <span className="whitespace-pre-wrap [overflow-wrap:anywhere]">{turn.note || (turn.status === 'pending' ? 'The change was not kept.' : '')}</span>
                    {live && (
                      <span className="flex items-center gap-1.5 flex-wrap">
                        <button ref={keepRef} type="button" onClick={keepChanges} className="ns-btn ns-btn-xs ns-btn-primary ns-btn-flat" data-screenplay-keep><Check size={14} aria-hidden="true" />Keep changes</button>
                        <button type="button" onClick={undoChanges} className="ns-btn ns-btn-xs ns-btn-soft ns-btn-flat" data-screenplay-undo><Undo2 size={14} aria-hidden="true" />Undo</button>
                      </span>
                    )}
                    {turn.status === 'kept' && <span className="flex items-center gap-1 text-2xs font-bold text-mint-deep"><Check size={12} aria-hidden="true" /> Kept</span>}
                    {turn.status === 'written' && <span className="flex items-center gap-1 text-2xs font-bold text-mint-deep"><Check size={12} aria-hidden="true" /> Written</span>}
                    {turn.status === 'undone' && <span className="text-2xs font-bold text-ink-faint">Undone</span>}
                    {(turn.usd !== null || live) && <span className="ns-t-meta">{turn.model}{turn.usd !== null ? ` · ${formatCostShort(turn.usd)}` : ''}{live ? ' · a version of the page before is kept' : ''}</span>}
                  </div>
                )}
              </React.Fragment>
            );
          })}
          {asking && (
            <div className="self-start flex items-center gap-1.5 px-3 py-2 rounded-2xl rounded-bl-md bg-white border border-ink/10" aria-label="Writing" data-screenplay-asking>
              {[0, 1, 2].map(i => <span key={i} className={`w-1.5 h-1.5 shrink-0 rounded-full bg-lavender ${i === 1 ? '' : 'opacity-50'}`} />)}
            </div>
          )}
        </div>
        {/* The asking */}
        {mayChange && (
          <div className="flex flex-col gap-2 px-3 pt-2.5 pb-3 border-t border-ink/5 shrink-0" data-screenplay-ask-box>
            {chosen && chosenLines > 0 && (
              <div className="flex items-center gap-1.5">
                <span className="ns-chip ns-chip-sm ns-chip-lavender gap-1 pr-1" data-screenplay-chosen>
                  {chosenLines} line{chosenLines === 1 ? '' : 's'} chosen
                  <button type="button" onClick={() => { setChosen(null); setLetGo(n => n + 1); }} className="ns-icon-btn ns-icon-btn-xs" style={{ '--size': '18px' } as React.CSSProperties} aria-label="Let go of the chosen lines" data-screenplay-let-go><X /></button>
                </span>
                <span className="ns-t-meta">The model changes only these, if you ask about them.</span>
              </div>
            )}
            <div className="relative">
              <textarea
                value={chatInput}
                onChange={(e) => setChatInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
                rows={3}
                placeholder={blank ? 'A first draft: what the film is, in a few lines' : 'Ask for a scene, a rewrite, a cut. Or just write on the page.'}
                aria-label="Ask"
                disabled={asking || !!review}
                className="ns-field w-full resize-none pr-11 text-[13px] leading-snug"
                data-screenplay-chat-input
              />
              <button type="button" onClick={send} disabled={asking || !!review || !chatInput.trim() || !available} className="ns-icon-btn ns-icon-btn-solid absolute right-1.5 bottom-1.5" aria-label="Send" data-screenplay-send><ArrowUp /></button>
            </div>
            <span className="ns-t-meta" data-cost-estimate>
              {review ? 'Keep or undo the change on the page first.' : !available ? 'The model needs a Venice, Google or OpenAI key, added in Settings.' : `${chatCost !== null ? `Up to ${formatCost(chatCost)} · ` : ''}${modelName}${efforts.length > 0 && thinking ? ` · ${(THINKING_LABELS[thinking] ?? thinking).toLowerCase()}` : ''}`}
            </span>
          </div>
        )}
        {problem && <div className="ns-note mx-3 mb-3" data-tone="danger" role="alert">{problem}</div>}
      </aside>
    </div>,
    document.body,
  ) : null;

  return (
    <BoardCard
      id={idea.id}
      tone="lemon"
      mark={<span className="canvas-node-icon"><FileText /></span>}
      title={(
        <>
          {headTitle}
          <span className="ns-badge shrink-0" data-screenplay-draft>Draft {meta.draft}</span>
          {idea.locked && <Lock size={12} className="text-ink-faint shrink-0" aria-label={`Kept by ${idea.lockedByName ?? 'someone'}`} />}
        </>
      )}
      tools={!confirmDelete ? (
        <>
          {!empty && onOpen && (
            <button type="button" onClick={onOpen} className="ns-icon-btn ns-icon-btn-xs" aria-label="Open the screenplay" title="Open it full: the pages, the scenes and the chat with the model"><Maximize2 /></button>
          )}
          {canEditWorkspace && (
            <button type="button" onClick={() => act(() => onKeep(!idea.locked), 'The card could not be kept.')} className={`ns-icon-btn ns-icon-btn-xs ${idea.locked ? '' : 'node-reveal'}`} aria-pressed={idea.locked} aria-label={idea.locked ? 'Let go of this screenplay' : 'Keep this screenplay'} title={idea.locked ? `Kept by ${idea.lockedByName ?? 'someone'}: only they change it` : 'Keep it: nobody else changes it'}>
              {idea.locked ? <Unlock /> : <Lock />}
            </button>
          )}
          {canEditWorkspace && mayChange && (
            <button type="button" onClick={() => setConfirmDelete(true)} className="ns-icon-btn ns-icon-btn-xs ns-icon-btn-danger" aria-label="Delete this screenplay" title="Delete this screenplay and its versions"><Trash2 /></button>
          )}
        </>
      ) : null}
      highlighted={highlighted}
      data={{ 'data-screenplay-card': '', 'data-screenplay-opened': focused ? 'true' : undefined }}
    >
      {confirmDelete && !focused && (
        <div className="ns-note m-3" data-tone="danger" data-confirm-delete>
          <span className="flex-1">Delete this screenplay and its {meta.versions.length} version{meta.versions.length === 1 ? '' : 's'}?</span>
          <button type="button" onClick={() => { setConfirmDelete(false); act(() => onDelete(), 'The screenplay could not be deleted.'); }} className="ns-btn ns-btn-xs ns-btn-danger ns-btn-flat">Delete</button>
          <button type="button" onClick={() => setConfirmDelete(false)} className="ns-btn ns-btn-xs ns-btn-soft ns-btn-flat">Keep</button>
        </div>
      )}

      {empty ? (
        /* Nothing written yet: the title page, and a brief for the model, or a blank page to write on */
        <div className="grid grid-cols-[1fr_minmax(0,1.1fr)] gap-x-5 gap-y-3 px-4 pt-3 pb-4" data-screenplay-empty>
          {/* At the left, the title page and the way to write it by hand; at the right, the brief for the model */}
          <div className="flex flex-col gap-3">
            <label className="flex flex-col gap-1"><span className="ns-t-label">Title</span><input type="text" defaultValue={meta.title} maxLength={120} disabled={!mayChange} onBlur={(e) => { if (e.target.value.trim() !== meta.title) saveTitlePage({ title: e.target.value.trim() }); }} className="ns-field ns-field-sm" data-screenplay-field="title" /></label>
            <label className="flex flex-col gap-1"><span className="ns-t-label">Written by</span><input type="text" defaultValue={meta.author} maxLength={120} disabled={!mayChange} onBlur={(e) => { if (e.target.value.trim() !== meta.author) saveTitlePage({ author: e.target.value.trim() }); }} className="ns-field ns-field-sm" data-screenplay-field="author" /></label>
            <div className="flex flex-wrap items-center gap-2">
              <span className="ns-t-label">Paper</span>
              {paperSwitch}
            </div>
            <p className="ns-t-meta">A sheet of {meta.paper} in Courier 12, every line in its place: Enter makes the line that usually follows, Tab changes what a line is, INT. or EXT. starts a scene heading by itself.</p>
            <button type="button" disabled={!mayChange || busy} onClick={startWriting} className="ns-btn ns-btn-sm ns-btn-primary ns-btn-flat self-start" data-screenplay-start>Write it yourself</button>
          </div>
          <div className="flex flex-col gap-3">
            <label className="flex flex-col gap-1">
              <span className="ns-t-label">Or a brief for the model</span>
              <textarea value={brief} onChange={(e) => setBrief(e.target.value)} disabled={!mayChange} rows={4} placeholder="A 2 minute short: a small fox who cannot keep a lantern lit, at a harbour at dusk. Warm, not sad. Almost no dialogue." className="ns-field w-full resize-none font-serif text-[15px] leading-7" data-screenplay-brief />
            </label>
            <div className="flex flex-wrap items-center gap-2">
              <span className="ns-t-label">Length</span>
              <select value={pages} onChange={(e) => setPages(Number(e.target.value))} aria-label="How many pages" className="ns-field ns-field-sm w-[160px]">
                {LENGTHS.map(n => <option key={n} value={n}>{n} page{n === 1 ? '' : 's'} · about {n} min</option>)}
              </select>
            </div>
            {available && mayChange && modelControls}
            {!available && mayChange && <div className="ns-note" data-tone="warn">The model needs a Venice, Google or OpenAI key, added in Settings. You can still write it yourself.</div>}
            {cost !== null && <span className="ns-t-meta" data-cost-estimate>Writing: up to {formatCost(cost)} from your Venice balance.{efforts.length > 0 ? ' Thinking is paid for too.' : ''}</span>}
            <button type="button" disabled={!mayChange || !available || busy} onClick={ask} className="ns-btn ns-btn-sm ns-btn-soft ns-btn-flat self-start" data-screenplay-write>
              {busy ? <Loader2 size={14} className="animate-spin" aria-hidden="true" /> : <Sparkles size={14} aria-hidden="true" />}
              {busy ? 'Writing' : `Write ${pages} page${pages === 1 ? '' : 's'}`}
            </button>
          </div>
        </div>
      ) : focused ? (
        /* Opened over the board: the card keeps its place and its size while the writing happens in the opened view */
        <div className="m-3 rounded-lg bg-ink/[0.04] grid place-items-center text-sm text-ink-faint" style={{ height: pageHeight + 72 }} data-screenplay-shell>
          <span className="flex items-center gap-2"><Maximize2 size={16} aria-hidden="true" /> Opened</span>
        </div>
      ) : (
        <>
          {/* The bar over the page: how long it is, the pager (the one way to turn pages here), the ways in */}
          <div className="flex flex-wrap items-center gap-1.5 px-3 py-2 border-b border-ink/5">
            <span className="ns-t-meta whitespace-nowrap" data-screenplay-meta>{pageCount} page{pageCount === 1 ? '' : 's'} · {minutesOf(pageCount)} · {meta.paper}</span>
            {idea.madeBy && <span className="ns-chip ns-chip-sm" title={`Last written by ${idea.madeBy.model}`}><Sparkles size={12} aria-hidden="true" />{idea.madeBy.model}</span>}
            <span className="flex-1" />
            {view === 'page' && pager}
            {viewSwitch}
            <button type="button" disabled={!mayChange || !available} onClick={() => setPanel(panel === 'write' ? null : 'write')} aria-pressed={panel === 'write'} className="ns-btn ns-btn-xs ns-btn-flat ns-btn-soft" data-screenplay-open="write"><Sparkles size={14} aria-hidden="true" />Write</button>
            <button type="button" onClick={() => setPanel(panel === 'versions' ? null : 'versions')} aria-pressed={panel === 'versions'} className="ns-btn ns-btn-xs ns-btn-flat ns-btn-soft" data-screenplay-open="versions"><History size={14} aria-hidden="true" />Versions · {meta.versions.length}</button>
            <button type="button" onClick={() => setPanel(panel === 'export' ? null : 'export')} aria-pressed={panel === 'export'} className="ns-btn ns-btn-xs ns-btn-flat ns-btn-soft" data-screenplay-open="export"><Download size={14} aria-hidden="true" />Export</button>
          </div>

          {/* The page, one at a time (the pager turns them; the wheel is the board's); or the words as Fountain */}
          <div ref={bodyRef} className="relative px-3 pt-2 pb-3">
            {panel && (
              <div className="absolute left-3 right-3 top-2 z-20 ns-panel p-3 flex flex-col gap-2" data-screenplay-panel={panel}>
                <div className="flex items-center gap-2">
                  <span className="ns-t-label flex-1">{panel === 'write' ? `Ask ${modelName ?? 'the model'}` : panel === 'versions' ? 'Versions' : 'Export'}</span>
                  <button type="button" onClick={() => setPanel(null)} className="ns-icon-btn ns-icon-btn-xs" aria-label="Close"><X /></button>
                </div>
                {panel === 'write' && composer}
                {panel === 'versions' && <div className="flex flex-col gap-2" data-screenplay-versions>{saveVersion}{versionList}</div>}
                {panel === 'export' && exportChoices}
              </div>
            )}
            {view === 'page' ? (
              <div ref={scrollerRef} onScroll={onScroll} className="relative overflow-hidden rounded-lg bg-ink/[0.04]" style={{ height: pageHeight + 16 }} data-screenplay-pages data-pages-scroller>
                {pagesInScroller}
              </div>
            ) : fountainField}
          </div>
        </>
      )}

      {problem && !focused && <div className="ns-note mx-3 mb-3" data-tone="danger" role="alert">{problem}</div>}
      {focusView}
    </BoardCard>
  );
};

export default ScreenplayCard;
